import * as React from "react";
import dayjs from "dayjs";

import { Box, Grid } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomButton from "../../../../helper-components/input/CustomButton";
import ControlledAccordion from "../../../../helper-components/form/ControlledAccordion";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { querySelectAuthors } from "../../../../helper-functions/api/authorQueries";
import { querySelectLibraries } from "../../../../helper-functions/api/libraryQueries";
import { validateRow } from "../../../../helper-functions/validation/objectValidation";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { IS_RESOURCE_VALID, RESOURCE_VALIDATION } from "../../../../helper-functions/validation/resourceValidation";

import { getLanguage } from "../../../../config/language/language";
import { RESOURCE_REQUEST_OBJECT, RESOURCE_TYPES } from "../../../../config/constants/Constants";

/**
 * A form to update a many resources.
 * Set data based on first selected row.
 */
export default function ResourceMultiEditForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = RESOURCE_REQUEST_OBJECT;
    const validation = RESOURCE_VALIDATION;
    const isValid = IS_RESOURCE_VALID;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            // Convert to dayjs format if datetime exists.
            uploaded_on: existingRow.uploaded_on ? dayjs(existingRow.uploaded_on) : null,
            created_on: existingRow.created_on ? dayjs(existingRow.created_on) : null,
            updated_on: existingRow.updated_on ? dayjs(existingRow.updated_on) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newResource = JSON.parse(JSON.stringify(row));
        setValue(newResource, key, value);

        validateRow(obj, validation, newResource);
        setInsertObjectState(setRow, newResource);
    };

    const handleAuthors = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "author_ids", ids);
    };

    const handleLibrary = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "library_id", ids?.[0] ?? null);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const options = { row };
        callback(true, options);
    };

    const accordionValues = [
        {
            title: "Authors",
            description: "Select one or more authors",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleAuthors}
                    query={querySelectAuthors}
                    selected={existingRow.authors}
                    getPrimarySearchText={(el) => el?.first_name}
                    getSecondarySearchText={(el) => el?.last_name}
                    getPrimaryText={(el) => `${el?.first_name} ${el?.last_name}`}
                    getSecondaryText={(el) => getEllipsis(el?.bio, 50)}
                />
            )
        },
        {
            title: "Library",
            description: "Select the library",
            component: (
                <CustomSelection
                    {...props}
                    callback={handleLibrary}
                    query={querySelectLibraries}
                    selected={existingRow.library ? [existingRow.library] : null}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        }
    ];

    return (
        <Box className="box">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomInput name="type" row={row} onChange={onChange} label="Type" select={RESOURCE_TYPES} />
                </Grid>

                <Grid item xs={12}>
                    <ControlledAccordion {...props} startClosed values={accordionValues} />
                </Grid>
            </Grid>

            <CustomButton {...props} title={"Update resources"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
